import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const team = [
  {
    name: "Esp. Yanneth Akchar",
    role: "Especialista en Neurodesarrollo",
    areas: ["Terapia de Lenguaje", "TEA", "Evaluaciones integrales"],
    initials: "YA",
    color: "#1a6b7c",
  },
  {
    name: "Esp. Elio Avilan",
    role: "Especialista en Conducta",
    areas: ["Terapia ABA", "TDAH", "Intervención conductual"],
    initials: "EA",
    color: "#1e3a5f",
  },
  {
    name: "Esp. Valeria Carrera",
    role: "Terapeuta Ocupacional",
    areas: ["Integración sensorial", "Motricidad", "TCC"],
    initials: "VC",
    color: "#5a9a8a",
  },
];

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const cardAnim = {
  hidden:  { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function Team() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="equipo"
      ref={ref}
      className="py-24 bg-muted"
      aria-labelledby="team-heading"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-14"
        >
          <p className="font-sans text-xs font-semibold tracking-widest text-primary uppercase mb-3">
            Nuestro Equipo
          </p>
          <h2 id="team-heading" className="section-heading text-3xl sm:text-4xl mb-4">
            Profesionales comprometidos con tu bienestar
          </h2>
          <div className="w-12 h-0.5 bg-primary rounded-full" aria-hidden="true" />
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid sm:grid-cols-3 gap-7"
          role="list"
        >
          {team.map(({ name, role, areas, initials, color }) => (
            <motion.article
              key={name}
              variants={cardAnim}
              role="listitem"
              className="card-base overflow-hidden group"
              aria-label={`${name}, ${role}`}
            >
              {/* Photo area */}
              <div
                className="relative h-52 flex items-center justify-center"
                style={{ background: `${color}0d` }}
              >
                {/* Placeholder avatar */}
                <div
                  className="w-28 h-28 rounded-full flex items-center justify-center font-serif font-bold text-3xl text-white shadow-md"
                  style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
                  aria-hidden="true"
                >
                  {initials}
                </div>

                {/* Top accent strip */}
                <div
                  className="absolute top-0 inset-x-0 h-1"
                  style={{ background: color }}
                  aria-hidden="true"
                />
              </div>

              {/* Content */}
              <div className="p-7">
                <h3 className="font-serif font-bold text-lg text-navy mb-1 leading-snug">
                  {name}
                </h3>
                <p className="font-sans text-sm font-medium text-primary mb-4">{role}</p>

                <ul className="flex flex-col gap-1.5" aria-label={`Áreas de ${name}`}>
                  {areas.map((area) => (
                    <li
                      key={area}
                      className="flex items-center gap-2 font-sans text-sm text-txtMuted"
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: color }}
                        aria-hidden="true"
                      />
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </motion.div>

        {/* Note about placeholders */}
        <p className="font-sans text-xs text-txtMuted text-center mt-8 opacity-60">
          Las fotos de los especialistas se actualizarán próximamente.
        </p>
      </div>
    </section>
  );
}
